/**
 * ResolutionActivationObservability (AJIP-16.02.006 · WS13)
 * ---------------------------------------------------------------------
 * Nineteen canonical event kinds for Resolution & Activation. Same
 * emitter pattern as prior packages. Internal instrumentation only:
 *   * No customer profiling.
 *   * No provider-specific telemetry.
 *   * No dashboards.
 *   * No I/O, no network calls.
 */

const RESOLUTION_ACTIVATION_EVENTS = Object.freeze({
  RESOLUTION_REQUESTED: 'RESOLUTION_REQUESTED',
  RESOLUTION_COMPLETED: 'RESOLUTION_COMPLETED',
  RESOLUTION_REJECTED: 'RESOLUTION_REJECTED',
  VERSION_RESOLVED: 'VERSION_RESOLVED',
  VERSION_REJECTED: 'VERSION_REJECTED',
  CONFLICT_DETECTED: 'CONFLICT_DETECTED',
  ACTIVATION_REQUESTED: 'ACTIVATION_REQUESTED',
  ACTIVATION_AUTHORISED: 'ACTIVATION_AUTHORISED',
  ACTIVATION_REJECTED: 'ACTIVATION_REJECTED',
  ACTIVATION_COMPLETED: 'ACTIVATION_COMPLETED',
  ACTIVATION_FAILED: 'ACTIVATION_FAILED',
  EXISTING_JOURNEY_REUSED: 'EXISTING_JOURNEY_REUSED',
  JOURNEY_RESTORATION_REQUESTED: 'JOURNEY_RESTORATION_REQUESTED',
  JOURNEY_RESTORED: 'JOURNEY_RESTORED',
  IDEMPOTENT_REQUEST_REPLAYED: 'IDEMPOTENT_REQUEST_REPLAYED',
  SUSPEND_REQUESTED: 'SUSPEND_REQUESTED',
  RESUME_REQUESTED: 'RESUME_REQUESTED',
  CANCEL_REQUESTED: 'CANCEL_REQUESTED',
  COMPLETION_REQUESTED: 'COMPLETION_REQUESTED',
});

const ALL_RESOLUTION_ACTIVATION_EVENTS = Object.freeze(Object.values(RESOLUTION_ACTIVATION_EVENTS));

class ResolutionActivationObservability {
  constructor() {
    this._handlers = new Map();
    for (const k of ALL_RESOLUTION_ACTIVATION_EVENTS) this._handlers.set(k, new Set());
    this._enabled = true;
  }

  on(kind, handler) {
    if (!ALL_RESOLUTION_ACTIVATION_EVENTS.includes(kind)) return () => {};
    if (typeof handler !== 'function') return () => {};
    this._handlers.get(kind).add(handler);
    return () => this._handlers.get(kind).delete(handler);
  }

  emit(kind, payload) {
    if (!this._enabled) return 0;
    if (!ALL_RESOLUTION_ACTIVATION_EVENTS.includes(kind)) return 0;
    const handlers = this._handlers.get(kind);
    let count = 0;
    for (const h of handlers) {
      try {
        h(Object.freeze({ event: kind, at: Date.now(), payload }));
        count += 1;
      } catch (_e) {
        // Handler failures must never affect resolution or activation. Isolate.
      }
    }
    return count;
  }

  disable() { this._enabled = false; }
  enable() { this._enabled = true; }
  isEnabled() { return this._enabled; }
  listenerCount(kind) {
    if (!ALL_RESOLUTION_ACTIVATION_EVENTS.includes(kind)) return 0;
    return this._handlers.get(kind).size;
  }
  clear() { for (const set of this._handlers.values()) set.clear(); }
}

export {
  RESOLUTION_ACTIVATION_EVENTS,
  ALL_RESOLUTION_ACTIVATION_EVENTS,
  ResolutionActivationObservability,
};
